import React, { useEffect, useState } from "react";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import AddCategoryView from "./AddCategoryView";

import { DragIcon, PlusThinWhiteIcon, EditIcon, DeleteRedIcon, } from "../../../assets/icons";
import { CategorySettingsItemDto } from "../../../models/categories/categories.response";
import { useNotification } from "../../toster-notification/NotificationContext";
import { categoriesService } from "../../../services/categoriesService";
import { useParams } from "react-router-dom";
import Loader from "../../shared/Loader";
import EditCategoryView from "./EditCategoryView";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableCategoryRow from "./SortableCategoryRow";
import { confirmDialog } from "../../../utils/confirmDialog";

const CategoryPage: React.FC = () => {

    const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
    const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState<string>("");

    const [categories, setCategories] = useState<CategorySettingsItemDto[]>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotification();

    const param = useParams();
    const localId = param.localId

    useEffect(() => {
        fetchCategories();
    }, [localId]);

    const fetchCategories = async () => {
        setLoading(true);

        try {
            const response = await categoriesService.getSettingsCategories(localId!);

            if (response.success && response.data) {
                setCategories(response.data);
            } else {
                notify("error", "Greška pri učitavanju kategorija");
            }
        } catch (error: any) {
            console.error("Error fetching categories:", error);
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Greška pri učitavanju kategorija");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCategory = async (categoryId: string) => {

        if(localId === undefined || localId === null) {
            notify("error", "Ne može se obrisati kategorija. Nedostaje ID lokala.");
            return;
        }

        let confirm = await confirmDialog({
            title: "Da li ste sigurni da želite da obrišete ovu kategoriju?",
            description: "Ova akcija je nepovratna.",
            isDelete: true,
        });

        if (!confirm) {
            return;
        }

        setLoading(true);

        try {
            const response = await categoriesService.deleteCategory(localId, categoryId);

            if (response.success && response.message) {
                notify("success", "Kategorija uspešno obrisana.");
                fetchCategories();
            } else {
                notify("error", "Greška pri brisanju kategorije.");
            }
        } catch (error: any) {
            console.error("Error deleting category:", error);
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Greška pri brisanju kategorije.");
            }
        } finally {
            setLoading(false);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    const persistOrder = async (updated: CategorySettingsItemDto[]) => {

        if(localId === undefined || localId === null) {
            notify("error", "Local ID nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const orderRecord: Record<string, number> = {};
            updated.forEach((c) => {
                orderRecord[c.id] = c.order;
            });
            
            const response = await categoriesService.sortCategories(localId, {categories: orderRecord, localId});

            if (response.success) {
                notify("success", "Redosled kategorija uspešno sačuvan.");
            } else {
                notify("error", "Greška pri čuvanju redosleda kategorija.");
            }
        } catch (error: any) {
            console.error("Error updating categories order:", error);
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Greška pri čuvanju redosleda kategorija.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) return;
        if (active.id === over.id) return;

        setCategories((prev) => {
            const oldIndex = prev.findIndex((c) => c.id === active.id);
            const newIndex = prev.findIndex((c) => c.id === over.id);

            const moved = arrayMove(prev, oldIndex, newIndex);

            const updated = moved.map((c, idx) => ({
                ...c,
                order: idx + 1,
            }));

            // 3) pozovi snimanje (side-effect van setState-a)
            queueMicrotask(() => persistOrder(updated));

            return updated;
        });
    };



    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {isAddCategoryModalOpen && (
                <AddCategoryView
                    onClose={() => {
                        setIsAddCategoryModalOpen(false)
                        fetchCategories();
                    }}
                />
            )}

            {isEditCategoryModalOpen && (
                <EditCategoryView
                    categoryId={editingCategoryId}
                    onClose={() => {
                        setIsEditCategoryModalOpen(false)
                        fetchCategories();
                    }}
                />
            )}

            <section className="category-section">
                <div className="w-layout-blockcontainer container category-container w-container">
                    <div className="category-wrapper">
                        <div className="header-div">
                            <div>
                                <h2 className="heading-3">Kategorije</h2>
                                <div className="text-block">
                                    Upravljajte glavnim kategorijama menija<br />
                                </div>
                            </div>
                            <div className="header-button-div" onClick={() => setIsAddCategoryModalOpen(true)}>
                                <div className="button-primary new-category">
                                    <img loading="lazy" src={PlusThinWhiteIcon} alt="" className="button-image" />
                                    <div className="button-text">Nova kategorija</div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-settings-box-div category-list-block">
                            <div className="list-header">
                                <div className="list-header-info">
                                    <div className="list-header-title">Sve kategorije</div>
                                    <div className="list-header-description">
                                        Ukupno kategorija: 3 (3 aktivnih)<br />
                                    </div>
                                </div>
                            </div>
                            <div className="list-div category">
                                <div className="list-row-div list-header-row">
                                    <div className="div-block"></div>
                                    <div>Naziv</div>
                                    <div>Slug</div>
                                    <div>Status</div>
                                    <div id="w-node-_784b75b9-7580-94fe-59b3-83eda50769d8-1cee91cf">Akcije</div>
                                </div>

                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={categories.map((c) => c.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {categories
                                            .slice()
                                            .sort((a, b) => a.order - b.order)
                                            .map((category) => (
                                                <SortableCategoryRow
                                                    key={category.id}
                                                    category={category}
                                                    onEdit={(id) => {
                                                        setIsEditCategoryModalOpen(true);
                                                        setEditingCategoryId(id);
                                                    }}
                                                    onDelete={handleDeleteCategory}
                                                />
                                            ))}
                                    </SortableContext>
                                </DndContext>


                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default CategoryPage;